import React, { useMemo, useState, Children, useCallback } from 'react';

import { CommonModal, CommonModalProps } from '../CommonModal';
import style from './index.module.css';

export type ModalStepProps<
  T,
  R =
    | (T & {
        [x: string]: any;
      })
    | undefined,
> = {
  ctx?: R;
  setCtx?: React.Dispatch<any>;
  showNext?(): void;
  showPre?(): void;
  onClose?(): void;
  currentStep?: number;
};

type ModalStepsProps<T> = {
  isOpen?: boolean;
  onRequestClose?(): void;
  children: Array<React.ReactElement<ModalStepProps<T>> | null>;
  initialContext?: T;
  modalClassName?: string;
} & Pick<CommonModalProps, 'size'>;

export const Steps = <T,>({
  children,
  onRequestClose,
  isOpen = true,
  initialContext,
  modalClassName,
  size,
}: ModalStepsProps<T>) => {
  const [step, setStep] = useState(0);
  const [ctx, setCtx] = useState<any>(initialContext);
  const validChildren = useMemo(() => Children.toArray(children).filter((x, i) => !!x), [children]);
  const stepsCount = useMemo(() => Children.count(validChildren) || 0, [validChildren]);

  const showNext = useCallback(() => {
    if (!stepsCount || stepsCount - 1 === step) {
      onRequestClose?.();
      return;
    }
    setStep(step + 1);
  }, [onRequestClose, setStep, step, stepsCount]);

  const showPre = useCallback(() => {
    setStep(x => (x > 0 ? x - 1 : 0));
  }, [setStep]);

  return (
    <CommonModal
      className={modalClassName}
      containerClassName={style.modal}
      onRequestClose={onRequestClose}
      on={isOpen}
      size={size}>
      <div className={style.modalSteps}>
        <div className={style.modalStep}>
          {/* @ts-ignore */}
          {React.cloneElement(validChildren[step]!, {
            ctx,
            setCtx,
            showNext,
            showPre,
            currentStep: step,
            onClose: onRequestClose,
          })}
        </div>
      </div>
    </CommonModal>
  );
};
